import * as yup from 'yup';
import { Credit, CreditType } from '@src/features/api/ScheduleApi';
import { IOrderTransaction } from '@src/features/api/OrderApi';
import { PaymentMethod } from '@src/features/customers/types';
import type { IIssueBusinessGiftCardRequest } from '@src/features/api/CreditLedgerApi';
import { ILocationForm } from '@src/features/business/locations/types';
import {
  IGiftCard,
  IGiftCardBulkConfigForm,
  IGiftCardCustomerDetails,
  IIssueStoreCreditForm,
  IPaged,
  IPurchaseGiftCardForm,
} from './types';

const roundMoney = (value: number) => Math.round(value * 100) / 100;

export interface IGiftCardAllocation {
  giftCard: IGiftCard;
  amountApplied: number;
}

export interface IGiftCardAllocationResult {
  allocations: IGiftCardAllocation[];
  totalApplied: number;
  remainingDue: number;
}

/**
 * Mirrors the allocation the order API performs in CreditProcessor: cards are consumed in the order
 * they were applied, each covering min(balance, remaining), and any card beyond the amount owed is
 * skipped rather than producing a $0 transaction.
 */
export const allocateGiftCards = (
  giftCards: IGiftCard[],
  total: number
): IGiftCardAllocationResult => {
  let remaining = roundMoney(Math.max(total, 0));
  const allocations = giftCards.map((giftCard) => {
    const amountApplied = roundMoney(Math.min(giftCard.balance, remaining));
    remaining = roundMoney(remaining - amountApplied);
    return { giftCard, amountApplied };
  });

  return {
    allocations,
    totalApplied: roundMoney(Math.max(total, 0) - remaining),
    remainingDue: remaining,
  };
};

export const getGiftCardsTotalBalance = (giftCards: IGiftCard[]) =>
  roundMoney(
    giftCards.reduce((total, giftCard) => total + giftCard.balance, 0)
  );

/**
 * The order API counts gift cards already captured on the order towards the limit, so staff cannot
 * work around it across several payments. A card that is already captured does not consume another
 * slot when it is applied again, for example when a tip is added to an order it already paid for.
 */
export const countDistinctGiftCardsOnOrder = (
  capturedGiftCardIds: string[],
  selectedGiftCards: IGiftCard[]
) => {
  const distinctIds = new Set(capturedGiftCardIds);
  selectedGiftCards.forEach((giftCard) => distinctIds.add(giftCard.id));
  return distinctIds.size;
};

/**
 * Builds the credits payload sent to the order API. Every applied card travels in one request, which
 * is how the API expects them: it de-duplicates, applies each in turn and skips any card beyond the
 * amount owed.
 */
export const buildGiftCardCredits = (
  giftCards: IGiftCard[]
): Credit[] | null => {
  const usableGiftCards = giftCards.filter((giftCard) => giftCard.balance > 0);
  if (usableGiftCards.length === 0) {
    return null;
  }
  return usableGiftCards.map((giftCard) => ({
    type: CreditType.GiftCard,
    paymentToken: giftCard.code,
    isGlobal: giftCard.canBeUsedAtOtherBusinesses,
  }));
};

/**
 * The gift card behind a transaction is the one recorded on that transaction's own paymentSourceId,
 * so an order paid with several cards resolves a different card per row. Only gift card payments
 * carry a gift card there - a card payment's paymentSourceId is a saved credit card, and a refund to
 * a gift card carries the new card's id in refundPaymentId instead.
 */
export const getTransactionGiftCardId = (transaction?: IOrderTransaction) =>
  transaction?.paymentMethod === PaymentMethod.GiftCard
    ? transaction.paymentSourceId ?? ''
    : '';

/**
 * The code to show for a gift card transaction. The order records the code it charged (#2216), and that
 * is the only source that can name a card the business cannot read back from credit-ledger - a
 * SPACCURATE card has no owning business, so every business-scoped read misses it. Payments captured
 * before #2216 stored no code, so the fetched card answers for those.
 */
export const getTransactionGiftCardCode = (
  transaction?: IOrderTransaction,
  fetchedGiftCard?: IGiftCard
) => transaction?.giftCardCode || fetchedGiftCard?.code || '';

export const GiftCardCsvSchema: yup.SchemaOf<IGiftCardBulkConfigForm> =
  yup.object({
    businessId: yup.string().required('Business is required'),
    locationId: yup.string().required('Location is required'),
    origin: yup.string().required('Origin is required'),
    canBeUsedAtOtherLocations: yup.boolean().default(true),
  });

export const PurchaseGiftCardSchema: yup.SchemaOf<IPurchaseGiftCardForm> =
  yup.object({
    type: yup.mixed().oneOf(['Digital', 'Physical']).required('required'),
    amount: yup
      .number()
      .min(1, 'Gift Card amount must be $1 or more')
      .required('required'),
    senderName: yup
      .string()
      .default('')
      .when('type', {
        is: 'Digital',
        then: yup.string().required('Sender name is required'),
        otherwise: yup.string().default(''),
      }),
    recipientName: yup
      .string()
      .default('')
      .when('type', {
        is: 'Digital',
        then: yup.string().required('Recipient name is required'),
        otherwise: yup.string().default(''),
      }),
    recipientEmail: yup
      .string()
      .default('')
      .when('type', {
        is: 'Digital',
        then: yup
          .string()
          .email('must be a valid email address')
          .required('Recipient email is required'),
        otherwise: yup.string().default(''),
      }),
    message: yup.string().default(''),
    customCode: yup
      .string()
      .default('')
      .when('type', {
        is: 'Physical',
        then: yup.string().required('Custom code is required'),
        otherwise: yup.string().default(''),
      }),
    locationId: yup
      .string()
      .default('')
      .when('type', {
        is: 'Physical',
        then: yup.string().required('Location is required'),
        otherwise: yup.string().default(''),
      }),
    canBeUsedAtOtherLocations: yup.boolean().default(false),
    id: yup.string().default(''),
    code: yup.string().default(''),
  });

/**
 * Client-side checks for Issue Store Credit (#2381). They mirror the ones credit-ledger enforces (#2380) so
 * the form fails fast; the code rules (no SPAC prefix, unique in the business) and the past-expiry rule
 * are left to the API, whose message the form shows.
 */
export const IssueStoreCreditSchema: yup.SchemaOf<IIssueStoreCreditForm> =
  yup.object({
    locationId: yup.string().required('Location is required'),
    amount: yup
      .number()
      .typeError('Amount is required')
      .moreThan(0, 'Amount must be greater than $0')
      .required('Amount is required'),
    code: yup.string().default(''),
    expires: yup.string().default(''),
    from: yup.string().default(''),
    to: yup.string().default(''),
    message: yup.string().default(''),
    reason: yup.string().trim().required('Reason is required'),
    canBeUsedAtOtherLocations: yup.boolean().default(true),
  });

/** Maps the form to the API body: a blank code or expiry is omitted, so the API generates or leaves it. */
export const toIssueBusinessGiftCardRequest = (
  form: IIssueStoreCreditForm
): IIssueBusinessGiftCardRequest => ({
  locationId: form.locationId,
  canBeUsedAtOtherLocations: form.canBeUsedAtOtherLocations,
  amount: Number(form.amount),
  code: form.code.trim() || undefined,
  expires: form.expires || undefined,
  from: form.from.trim(),
  to: form.to.trim(),
  message: form.message.trim(),
  reason: form.reason.trim(),
});

/**
 * The locations a user may issue store credit at: those they hold a "loc" claim for, which is what
 * credit-ledger checks (#2380).
 */
export const filterLocationsByClaims = (
  locations: ILocationForm[] | undefined,
  locationClaims: string[] | undefined
): ILocationForm[] =>
  (locations ?? []).filter((location) =>
    (locationClaims ?? []).includes(location.id)
  );

export const GiftCardCustomerDetailsSchema: yup.SchemaOf<IGiftCardCustomerDetails> =
  yup.object({
    name: yup.string().required('required'),
    email: yup
      .string()
      .email('must be a valid email address')
      .required('required'),
  });

/**
 * Maximum gift cards offered in the search autocomplete (#2214). The dropdown previously showed at
 * most four, because it sliced an unfiltered list to five and the first entry is the synthetic
 * "search for X" row.
 */
export const MAX_GIFT_CARD_SUGGESTIONS = 10;

export const EMPTY_GIFT_CARD_PAGE: IPaged<IGiftCard> = {
  items: [],
  currentPage: 1,
  resultsPerPage: 0,
  totalPages: 0,
  totalResults: 0,
};

/**
 * Normalises a gift card list response to a page (#2220).
 *
 * credit-ledger now answers both B2B list reads with a page envelope. The bare array is what it
 * answered with before, and this webapp is the only consumer of either route, so the two ship as a
 * pair - but a deploy is not atomic across two pipelines, and a webapp release that lands minutes
 * ahead of the API would otherwise render an empty grid for that window. A bare array is treated as
 * the single page it is: everything on page 1, one page in total.
 */
export const toGiftCardPage = (
  response: IPaged<IGiftCard> | IGiftCard[] | null | undefined
): IPaged<IGiftCard> => {
  if (!response) {
    return EMPTY_GIFT_CARD_PAGE;
  }
  if (Array.isArray(response)) {
    return {
      items: response,
      currentPage: 1,
      resultsPerPage: response.length,
      totalPages: response.length > 0 ? 1 : 0,
      totalResults: response.length,
    };
  }
  return { ...response, items: response.items ?? [] };
};

/**
 * Matches gift cards against typed text for the search autocomplete, prefix matches first.
 *
 * The shared autocomplete can filter for us, but only by mutating a single `filterKey` on every
 * suggestion — and the list it filters includes the synthetic blank "search for X" entry, whose empty
 * code matches nothing, so it would be dropped and the first REAL card would be rendered in its place
 * (the custom renderer keys the search row off index 0). Filtering here keeps that row intact.
 *
 * Codes are stored uppercase, so the comparison is uppercase on both sides.
 */
export const matchGiftCardsByCode = (
  giftCards: IGiftCard[],
  searchText: string,
  limit: number = MAX_GIFT_CARD_SUGGESTIONS
): IGiftCard[] => {
  const typed = searchText.trim().toUpperCase();
  if (!typed) {
    return [];
  }

  const prefixMatches: IGiftCard[] = [];
  const containsMatches: IGiftCard[] = [];
  giftCards.forEach((giftCard) => {
    const code = (giftCard.code ?? '').toUpperCase();
    if (code.startsWith(typed)) {
      prefixMatches.push(giftCard);
    } else if (code.includes(typed)) {
      containsMatches.push(giftCard);
    }
  });

  return [...prefixMatches, ...containsMatches].slice(0, limit);
};
